import autoBind from 'auto-bind';
import { createContext, ReactNode, use, useCallback, useEffect, useState } from 'react';
import { Environment, getEnvironment } from '../../../common/src/environment.ts';
import { HashedManifest, isSongFileMissing, SongFileExtension, SongManifestEntry } from '../../../common/src/model.ts';
import { musicManifestFilename } from '../../../common/src/paths.ts';
import { ensureUnreachable } from '../../../common/src/util.ts';
import {
  ClientCacheName,
  deleteUrlFromCache,
  DownloadFailedError,
  DownloadFunction,
  getStoredResponse,
  updateCacheResponse,
} from '../data/client_cache_storage.ts';
import {
  DownloadQueueAction,
  DownloadQueueEntry,
  DownloadQueueObjectStore,
  idbDelete,
  idbGet,
  idbGetAllKeys,
  idbPut,
} from '../data/idb.ts';
import { getChildUrlPrefix, getUrlForSongManifestEntry, UrlPrefix } from './path.ts';
import { useOnline } from './use_online.tsx';
import { withWebLock } from './web_locks.ts';

const msBetweenSuccessfulDownloads = 50;
const initialRetryDelayMs = 5000;
const maxRetryDelayMs = 5 * 60 * 1000;
const downloadQueueLockName = 'syp-download-queue';

// Injectable for tests (issue #3517) - production always uses the real clock and fetch.
let clock: () => number = () => Date.now();
let fetchImpl: typeof fetch = (...args) => fetch(...args);

export function setDownloadsClock(newClock: () => number) {
  clock = newClock;
}

export function setDownloadsFetch(newFetch: typeof fetch) {
  fetchImpl = newFetch;
}

export function resetDownloadsInjections() {
  clock = () => Date.now();
  fetchImpl = (...args) => fetch(...args);
}

// What a completed drain reports back: how many entries are still in the shared queue. A drain
// resolves as soon as nothing is DUE, which includes a drain whose every entry failed and backed
// off, and a drain this tab skipped because another tab holds the queue lock. Callers that record
// a "this client is current" hash must only do so on a clean drain (issue #3517 item B4).
export interface DownloadsCompleteResult {
  pending: number;
}

export type DownloadsCompleteFunction = () => Promise<DownloadsCompleteResult>;

interface DownloadsInterface {
  add: DownloadFunction;
  downloadsComplete: DownloadsCompleteFunction;
}

// "best effort" download of specified urls for offline cache population
// ok to add the same URL repeatedly b/c cache will satisfy instantly
export function useDownloads(): DownloadsInterface {
  return use(DownloadsContext);
}

const DownloadsContext = createContext<DownloadsInterface>({} as any);

export const DownloadsProvider = ({ children }: { children: ReactNode }) => {
  const [downloads] = useState<Downloads>(() => new Downloads());
  const isOnline = useOnline();

  const add = useCallback(
    async (url: string, entry: DownloadQueueEntry) => {
      await downloads.add(url, entry);
    },
    [downloads],
  );

  const downloadsComplete = useCallback(() => downloads.downloadsComplete(), [downloads]);

  useEffect(() => {
    downloads.setIsOnline(isOnline);
  }, [downloads, isOnline]);

  return <DownloadsContext value={{ add, downloadsComplete }}>{children}</DownloadsContext>;
};

type Callback = (result: DownloadsCompleteResult) => void;

// The queue drains under a cross-tab lock, rotates past entries that are backed off, and never
// drops an entry (issue #3517). A single failing download must not wedge the queue, because
// downloadsComplete() gates every later music update check.
export class Downloads {
  private downloading = false;
  private kickRequested = false;
  private isOnline = false;
  private callbacks: Callback[] = [];
  private cachedUrlPrefixes: Set<UrlPrefix> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private inFlightKick: Promise<void> | undefined;
  private disposed = false;
  private abandonedCount = 0;
  private skippedUncachedCount = 0;

  constructor() {
    autoBind(this);
  }

  async add(urlPrefix: UrlPrefix, entry: DownloadQueueEntry) {
    if (getEnvironment() === Environment.Dev) {
      console.log(`Queuing urlPrefix for download: ${urlPrefix}`);
    }
    const previousEntry = await idbGet(DownloadQueueObjectStore, urlPrefix);
    // retry metadata survives a re-add of the same content, but a new hash starts over
    const keepRetryMetadata = !!previousEntry && previousEntry.manifestEntry.hash === entry.manifestEntry.hash;
    const newEntry: DownloadQueueEntry = {
      action: getPreferredAction(previousEntry?.action, entry.action),
      manifestEntry: entry.manifestEntry,
      ...(keepRetryMetadata && previousEntry.attempts !== undefined ? { attempts: previousEntry.attempts } : {}),
      ...(keepRetryMetadata && previousEntry.nextAttemptAt !== undefined
        ? { nextAttemptAt: previousEntry.nextAttemptAt }
        : {}),
    };
    await idbPut(DownloadQueueObjectStore, newEntry, urlPrefix);
    void this.kick();
  }

  // resolves once nothing in the queue is currently due, reporting how many entries remain
  downloadsComplete(): Promise<DownloadsCompleteResult> {
    return new Promise<DownloadsCompleteResult>((resolve) => {
      this.addCallback(resolve);
    });
  }

  addCallback(callback: Callback) {
    this.callbacks.push(callback);
    void this.kick();
  }

  setIsOnline(isOnline: boolean) {
    this.isOnline = isOnline;
    void this.kick();
  }

  // Stops the queue: resolves once the drain in flight has finished, starts no other and cancels
  // the pending retry wake-up. Used by tests, which share one queue, so that one test's drain cannot
  // run on into the next test's entries; the provider's instance lives for the session.
  async dispose() {
    this.disposed = true;
    await this.inFlightKick;
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
  }

  private kick(): Promise<void> {
    if (this.disposed) {
      return Promise.resolve();
    }
    if (this.downloading) {
      // an add() or a callback registered while the drain is finishing up has no owner, so the
      // drain re-runs itself on the way out rather than stranding it (issue #3517)
      this.kickRequested = true;
      return Promise.resolve();
    }
    if (!this.isOnline) {
      return Promise.resolve();
    }
    this.downloading = true;
    this.inFlightKick = this.drainOrSkip();
    return this.inFlightKick;
  }

  private async drainOrSkip() {
    try {
      // one tab at a time drains the shared IndexedDB queue - a tab that cannot take the lock
      // skips the drain rather than stalling behind the tab that is doing the work
      const { ran } = await withWebLock(downloadQueueLockName, this.drainQueue, { ifAvailable: true });
      if (!ran) {
        await this.skipDrain();
      }
    } finally {
      this.downloading = false;
      if (this.kickRequested) {
        this.kickRequested = false;
        void this.kick();
      }
    }
  }

  // Another tab holds the queue lock and is draining it. Arm the retry timer as usual and report
  // every queued entry as pending, so callers do not record a hash for work this tab never saw.
  private async skipDrain() {
    await this.scheduleRetry();
    const { total } = await this.getQueueCounts();
    this.resolveCallbacks(total);
  }

  private async drainQueue() {
    for (;;) {
      for (;;) {
        const due = await this.getNextDueEntry();
        if (!due) {
          break;
        }
        const succeeded = await this.processEntry(due.urlPrefix, due.entry);
        if (succeeded) {
          await delay(msBetweenSuccessfulDownloads);
        }
      }
      await this.scheduleRetry();
      // a fresh look at the queue, because processing and scheduling above took several awaits
      // during which an add() may have landed - resolving callbacks now would resolve them for
      // work that has not happened yet
      const { due, total } = await this.getQueueCounts();
      if (due > 0) {
        continue;
      }
      this.logSummary();
      // synchronous from here, so nothing can slip in between the count and the callbacks
      this.resolveCallbacks(total);
      return;
    }
  }

  private async getQueueCounts(): Promise<{ due: number; total: number }> {
    const keys = await idbGetAllKeys(DownloadQueueObjectStore);
    const now = clock();
    let due = 0;
    for (const urlPrefix of keys) {
      const nextAttemptAt = (await idbGet(DownloadQueueObjectStore, urlPrefix))?.nextAttemptAt;
      // entries persisted by older bundles have no nextAttemptAt and are due now
      if (nextAttemptAt === undefined || nextAttemptAt <= now) {
        due += 1;
      }
    }
    return { due, total: keys.length };
  }

  private async getNextDueEntry(): Promise<{ urlPrefix: UrlPrefix; entry: DownloadQueueEntry } | undefined> {
    const keys = await idbGetAllKeys(DownloadQueueObjectStore);
    const now = clock();
    for (const urlPrefix of keys) {
      const entry = await idbGet(DownloadQueueObjectStore, urlPrefix);
      if (!entry) {
        console.log(`ERROR: Expected entry in download queue for key ${urlPrefix}`);
        await idbDelete(DownloadQueueObjectStore, urlPrefix);
        continue;
      }
      if (entry.nextAttemptAt !== undefined && entry.nextAttemptAt > now) {
        continue;
      }
      return { urlPrefix, entry };
    }
    return undefined;
  }

  private async processEntry(urlPrefix: UrlPrefix, entry: DownloadQueueEntry): Promise<boolean> {
    try {
      if (entry.manifestEntry.isManifest) {
        this.skippedUncachedCount += await refreshManifest({
          manifestUrl: urlPrefix,
          addDownload: this.add,
          entry,
          cachedUrlPrefixes: await this.getCachedUrlPrefixes(),
        });
      } else {
        await refreshSong({ urlPrefix, entry });
        // a song cached mid-batch must not look uncached to a later manifest walk (issue #3517)
        if (this.cachedUrlPrefixes && entry.action !== DownloadQueueAction.Delete) {
          this.cachedUrlPrefixes.add(urlPrefix);
        }
      }
      await idbDelete(DownloadQueueObjectStore, urlPrefix);
      return true;
    } catch (e: any) {
      if (!entry.manifestEntry.isManifest && isTerminalDownloadFailure(e)) {
        // A 4xx is the server saying the file is not there, and it will keep saying so until the
        // owning manifest publishes a new hash and re-queues the entry. Retrying forever would
        // hold downloadsComplete() - and therefore every hash - back for good. The cached copy is
        // left alone (issue #3517).
        //
        // A manifest is never abandoned: nothing above it re-queues it, and reporting its drain
        // as clean would record the very hash that says this client is up to date.
        this.abandonedCount++;
        console.log(`Abandoning download '${urlPrefix}' until its manifest changes: ${e.message}`);
        await idbDelete(DownloadQueueObjectStore, urlPrefix);
        return false;
      }
      const attempts = (entry.attempts ?? 0) + 1;
      const retryDelay = Math.min(initialRetryDelayMs * Math.pow(2, attempts - 1), maxRetryDelayMs);
      // expected transient during an outage - do not report as an error (issue #3517)
      console.log(
        `Failed download '${urlPrefix}' (attempt ${attempts}, retry in ${retryDelay} ms): ${e?.message ?? e}`,
      );
      await idbPut(DownloadQueueObjectStore, { ...entry, attempts, nextAttemptAt: clock() + retryDelay }, urlPrefix);
      return false;
    }
  }

  // wake the queue when the earliest backed-off entry comes due, so no external kick is needed
  private async scheduleRetry() {
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    let earliest: number | undefined;
    for (const urlPrefix of await idbGetAllKeys(DownloadQueueObjectStore)) {
      const nextAttemptAt = (await idbGet(DownloadQueueObjectStore, urlPrefix))?.nextAttemptAt;
      if (nextAttemptAt !== undefined && (earliest === undefined || nextAttemptAt < earliest)) {
        earliest = nextAttemptAt;
      }
    }
    if (earliest === undefined) {
      return;
    }
    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = undefined;
        void this.kick();
      },
      Math.max(0, earliest - clock()),
    );
  }

  private logSummary() {
    if (this.abandonedCount === 0 && this.skippedUncachedCount === 0) {
      return;
    }
    console.log(
      `Download queue: ${this.abandonedCount} download(s) abandoned until their manifest changes, ` +
        `${this.skippedUncachedCount} refresh(es) skipped for uncached songs`,
    );
    this.abandonedCount = 0;
    this.skippedUncachedCount = 0;
  }

  private resolveCallbacks(pending: number) {
    const callbacks = this.callbacks;
    this.callbacks = [];
    this.cachedUrlPrefixes = undefined;
    for (const callback of callbacks) {
      callback({ pending });
    }
  }

  private async getCachedUrlPrefixes(): Promise<Set<UrlPrefix>> {
    if (!this.cachedUrlPrefixes) {
      this.cachedUrlPrefixes = new Set<UrlPrefix>();
      for (const cacheName of Object.values(ClientCacheName)) {
        const cache = await caches.open(cacheName);
        for (const { url } of await cache.keys()) {
          const { pathname } = new URL(url);
          const prefix = pathname.match(/^(?<prefix>.+\/[^/%]+)%20-%20[^\\]+\.[a-zA-Z]+$/)?.groups?.prefix;
          if (prefix) {
            this.cachedUrlPrefixes.add(prefix);
          }
        }
      }
    }
    return this.cachedUrlPrefixes;
  }
}

// A 4xx means the file is not on the server, which no amount of retrying changes. Network throws
// (TypeError from fetch) and 5xx keep the unlimited backoff (issue #3517).
function isTerminalDownloadFailure(error: unknown): error is DownloadFailedError {
  return error instanceof DownloadFailedError && error.status >= 400 && error.status < 500;
}

async function delay(ms: number) {
  await new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function getPreferredAction(previousAction: DownloadQueueAction | undefined, newAction: DownloadQueueAction) {
  if (!previousAction) {
    return newAction;
  }
  // avoid clobbering 'delete' or 'refresh' with 'download'
  // also, 'delete-and-download' takes precedence over all other actions
  if (newAction === DownloadQueueAction.Download || previousAction === DownloadQueueAction.DeleteAndDownload) {
    return previousAction;
  }
  return newAction;
}

const cacheBypassQueryString = 'cache';
const cacheBypassQueryStringValue = 'no';

// returns the number of refreshes skipped because this client never cached the song
async function refreshManifest({
  manifestUrl,
  addDownload,
  entry: { action },
  cachedUrlPrefixes,
}: {
  manifestUrl: UrlPrefix;
  addDownload: DownloadFunction;
  entry: DownloadQueueEntry;
  cachedUrlPrefixes: Set<UrlPrefix>;
}): Promise<number> {
  let skippedUncachedCount = 0;
  // undefined when this device has no copy, which means "unknown", never "unchanged"
  const localManifest = await readStoredManifest(manifestUrl);
  const baseUrl = manifestUrl.substring(0, manifestUrl.length - (musicManifestFilename.length + 1));
  if (action === DownloadQueueAction.Delete) {
    await deleteLocalManifest();
    return skippedUncachedCount;
  }
  const { manifest: serverManifest, response } = await fetchServerManifest(manifestUrl);
  if (!response.ok) {
    // a failed fetch must not be mistaken for "the server has no manifest", which would delete
    // every local key below - retry with backoff instead (issue #3517)
    throw new DownloadFailedError(manifestUrl, response.status);
  }
  await deleteLocalKeysNotInServerManifest();
  await refreshUpdatedKeys();
  await updateCacheResponse(manifestUrl, response);
  return skippedUncachedCount;

  async function deleteLocalManifest() {
    if (localManifest) {
      // delete recursively
      for (const key of Object.keys(localManifest)) {
        await addChildDownload(localManifest, key, DownloadQueueAction.Delete);
      }
    }
    await deleteUrlFromCache(manifestUrl);
  }

  async function deleteLocalKeysNotInServerManifest() {
    if (localManifest) {
      for (const key of Object.keys(localManifest)) {
        if (!serverManifest || !(key in serverManifest)) {
          await addChildDownload(localManifest, key, DownloadQueueAction.Delete);
        }
      }
    }
  }

  async function refreshUpdatedKeys() {
    if (!serverManifest) {
      return;
    }
    for (const key of Object.keys(serverManifest)) {
      if (!localManifest) {
        // No local copy to compare with, so nothing below can be assumed current: every song
        // cached under this manifest is refreshed (addChildDownload skips the uncached ones). A
        // child manifest is followed only when this device holds something under it - a stored
        // copy or a cached song - so a first launch does not walk every church's custom music.
        if (!serverManifest[key].isManifest || (await isChildManifestOnDevice(key))) {
          await addChildDownload(serverManifest, key);
        }
        continue;
      }
      if (!(key in localManifest) || localManifest[key].hash !== serverManifest[key].hash) {
        await addChildDownload(serverManifest, key);
      }
    }
  }

  async function isChildManifestOnDevice(key: string): Promise<boolean> {
    const childManifestUrl = getChildUrlPrefix(baseUrl, key, true);
    const childDirectory = childManifestUrl.substring(0, childManifestUrl.length - musicManifestFilename.length);
    for (const cachedUrlPrefix of cachedUrlPrefixes) {
      if (cachedUrlPrefix.startsWith(childDirectory)) {
        return true;
      }
    }
    return !!(await getStoredResponse(childManifestUrl));
  }

  async function addChildDownload(manifest: HashedManifest, key: string, actionOverride?: DownloadQueueAction) {
    const manifestEntry = manifest[key];
    const childUrlPrefix = getChildUrlPrefix(baseUrl, key, manifestEntry.isManifest);
    if (!manifestEntry.isManifest && !cachedUrlPrefixes.has(childUrlPrefix)) {
      // expected for every song this client never cached - counted and summarized (issue #3517)
      skippedUncachedCount += 1;
      if (getEnvironment() === Environment.Dev) {
        console.log(`Ignoring refresh for uncached entry ${childUrlPrefix}`);
      }
      return;
    }
    await addDownload(childUrlPrefix, { action: actionOverride ?? action, manifestEntry });
  }
}

// will throw on network error - missing manifest indicates server did not have one
async function fetchWithCacheBypass(url: string, { bypassCache }: { bypassCache?: boolean } = {}): Promise<Response> {
  let response: Response;
  if (!bypassCache) {
    response = await fetchImpl(url);
  } else {
    const searchParams = new URLSearchParams({ [cacheBypassQueryString]: cacheBypassQueryStringValue });
    response = await fetchImpl(`${url}?${searchParams.toString()}`);
  }
  return response;
}

// The manifest as the server has it now. Will throw on network error.
async function fetchServerManifest(url: string): Promise<{ manifest?: HashedManifest; response: Response }> {
  const response = await fetchWithCacheBypass(url, { bypassCache: true });
  if (!response?.ok) {
    return { response };
  }
  const originalResponse = response.clone(); // clone() required before json body is read
  const manifest = (await response.json()) as HashedManifest;
  return { manifest, response: originalResponse };
}

// The manifest as this device has it, from the service worker's storage. Undefined when the device
// has no usable copy.
async function readStoredManifest(url: string): Promise<HashedManifest | undefined> {
  const response = await getStoredResponse(url);
  if (!response?.ok) {
    return undefined;
  }
  try {
    return (await response.json()) as HashedManifest;
  } catch {
    return undefined;
  }
}

async function refreshSong({ urlPrefix, entry }: { urlPrefix: UrlPrefix; entry: DownloadQueueEntry }) {
  const action = entry.action;
  const manifestEntry = entry.manifestEntry as SongManifestEntry;

  const { basePath, songNumber } = urlPrefix.match(/^(?<basePath>.*)\/(?<songNumber>[^/]+)$/)?.groups ?? {};

  if (!manifestEntry.extensions) {
    console.error(`Song manifest entry missing extensions: ${urlPrefix}, entry: ${JSON.stringify(entry)}`);
  }

  // manifest entries should always have .extensions, but we encountered an unexplained situation where they did not
  const extensions = [SongFileExtension.Syp, SongFileExtension.Mid, SongFileExtension.Pdf].filter(
    (extension) => !isSongFileMissing(manifestEntry, extension),
  );

  // Each file is refreshed independently: a song whose .syp is missing on the server must still
  // get its new .mid and .pdf, instead of leaving all three stale (issue #3517).
  const failures: unknown[] = [];
  for (const extension of extensions) {
    try {
      await refreshSongUrlForExtension(extension);
    } catch (error: any) {
      console.log(`Failed to refresh ${extension} for ${urlPrefix}: ${error?.message ?? error}`);
      failures.push(error);
    }
  }
  if (failures.length === 0) {
    return;
  }
  const terminalStatus = getTerminalStatus(failures);
  if (terminalStatus !== undefined) {
    // every failure was a 4xx, so the queue abandons the entry until the manifest hash changes
    throw new DownloadFailedError(urlPrefix, terminalStatus);
  }
  throw new Error(`${failures.length} of ${extensions.length} file(s) failed to download for ${urlPrefix}`);

  async function refreshSongUrlForExtension(extension: SongFileExtension) {
    if (!manifestEntry) {
      console.log(`ERROR: Unable to refresh cached song without manifest entry: ${urlPrefix}`);
      return;
    }
    const url = getUrlForSongManifestEntry({ basePath, songNumber, manifestEntry, extension });
    if (!url) {
      return;
    }
    switch (action) {
      case DownloadQueueAction.Delete:
        await deleteUrlFromCache(url);
        break;
      case DownloadQueueAction.Download:
        await downloadUrlIntoCache(url);
        break;
      case DownloadQueueAction.Refresh: {
        const response = await fetchWithCacheBypass(url, { bypassCache: true });
        await updateCacheResponse(url, response);
        break;
      }
      case DownloadQueueAction.DeleteAndDownload:
        await deleteUrlFromCache(url);
        await downloadUrlIntoCache(url);
        break;
      default:
        ensureUnreachable(action);
    }
  }
}

// The status a whole song entry should be treated as, if and only if every one of its files
// failed with a 4xx. A single transient failure keeps the entry on the unlimited backoff.
function getTerminalStatus(failures: unknown[]): number | undefined {
  if (!failures.every((failure) => isTerminalDownloadFailure(failure))) {
    return undefined;
  }
  return (failures[0] as DownloadFailedError).status;
}

async function downloadUrlIntoCache(url: string) {
  if (getEnvironment() === Environment.Dev) {
    console.log(`Downloading url into cache ${url}`);
  }
  await fetchImpl(url);
}
